import * as React from "react"
import { Modal, Form, Row, Col, Button, InputGroup, Table, Container, ProgressBar, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import ActionButton from "../ActionButton"
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import { FaChevronLeft, FaChevronUp, FaEdit } from "react-icons/fa"
import { of, Subject, Subscription, timer } from "rxjs"
import { switchMap, startWith, scan, takeWhile, map } from "rxjs/operators"
import { BirthdayInput } from "../FormattedInputs/BirthdayInput"
import { APIError } from "../../api/ObservableFromFetch"
import { format } from "date-fns"
import { MaxFileNameLength, MaxImageFileSize, MaxLogoImageWidth, MaxLogoImageHeight } from "../../config/Constants"
import FormatDate from "../../formatters/FormatDate"
import ImplementationImage from "../../api/Administration/Model/ImplementationImage"
import GetAllImplementationImageByImplementationID, { ImplementationImageData } from "../../api/Administration/GetAllImplementationImageByImplementationID"
import UpdateBrandingImage from "../../api/Administration/UpdateBrandingImage"
import EditActiveImageDate from "./EditActiveImageDate"
import PortalLogoInsert from "../../api/Invitation/PortalLogoInsert"
import PortalLogoModel from "../../api/Invitation/Model/PortalLogoModel"

interface ManageImagesProps {
  show: boolean
  implementationID: number
  onClose: () => void
}

interface ManageImagesUploadingState {
  fileName: string
  progressBarKey: string
  fileContent?: string
  isFileNameValid: boolean
  isFileSizeValid: boolean
  isFileTypeValid: boolean
  isLogoSizeValid: boolean
  uploadPercentage?: number
  uploading: boolean
  uploadError: boolean
  activeDate?: Date
  inactiveDate?: Date
  activeDateCanShowInvalid: boolean
  inactiveDateCanShowInvalid: boolean
}

interface ManageImagesSelectionState {
  selectionRight?: number
  activeImages: ImplementationImage[]
  inactiveImages: ImplementationImage[]
}

interface ManageImagesDatesEditState {
  editActiveDate: boolean
  data?: ImplementationImageData
}

interface ManageImagesState extends ManageImagesUploadingState, ManageImagesSelectionState, ManageImagesDatesEditState {
  loading: boolean
  saving: boolean
}

export default class ManageImages extends React.PureComponent<ManageImagesProps, ManageImagesState> {
  emptyState: ManageImagesState = {
    loading: false,
    saving: false,
    editActiveDate: false,
    progressBarKey: "progressBarKey",
    isFileNameValid: true,
    isFileSizeValid: true,
    isFileTypeValid: true,
    isLogoSizeValid: true,
    fileName: "",
    uploadPercentage: undefined,
    uploading: false,
    uploadError: false,
    activeDateCanShowInvalid: false,
    inactiveDateCanShowInvalid: false,
    activeDate: undefined,
    inactiveDate: undefined,
    activeImages: [],
    inactiveImages: []
  }
  state: ManageImagesState = { ...this.emptyState }

  file = React.createRef<HTMLInputElement>()
  activeDate = React.createRef<DatePicker>()
  inactiveDate = React.createRef<DatePicker>()
  loadSubscription?: Subscription
  saveSubscription?: Subscription
  uploadSubscription?: Subscription
  fileUploadSimulationSubscription?: Subscription
  fileUploadSimulation$ = new Subject<boolean>()

  componentDidMount() {
    this.fileUploadSimulationSubscription = this.fileUploadSimulation$.pipe(
      switchMap(enabled =>
        enabled
          ? timer(0, 500).pipe(
            startWith(5),
            scan((acc, curr) => acc + 5),
            takeWhile(value => value <= 90 && this.state.uploading),
            map(value => ({ value: value, write: true })))
          : of({ value: 100, write: false })
      )
    ).subscribe(
      (o: { value: number, write: boolean }) => o.write && this.setState({ uploadPercentage: o.value }),
      () => { }
    )
    this.setState({ loading: true })
  }

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.uploadSubscription && this.uploadSubscription.unsubscribe()
    this.fileUploadSimulationSubscription && this.fileUploadSimulationSubscription.unsubscribe()
  }

  componentDidUpdate(prevProps: ManageImagesProps, prevState: ManageImagesState) {
    if (!prevState.uploading && this.state.uploading) {
      this.fileUploadSimulation$.next(true)
    } else if (prevState.uploading && !this.state.uploading) {
      this.fileUploadSimulation$.next(false)
    }
    if (!prevState.loading && this.state.loading) {
      this.loadImplementationImages()
    }
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  loadImplementationImages = () => {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.loadSubscription = GetAllImplementationImageByImplementationID(this.props.implementationID).subscribe(
      data => this.setState({
        loading: false,
        activeImages: data.ImplementationActiveImages || [],
        inactiveImages: data.ImplementationInActiveImages || []
      }),
      error => this.handleAPIError(error, { loading: false })
    )
  }

  handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target && e.target.files && e.target.files[0]
    if (!file) {
      return
    }
    const fileName = file.name
    const fileType = file.type
    const ext = (fileName.lastIndexOf('.') >= 0) ? fileName.substring(fileName.lastIndexOf('.') + 1) : ""
    const isFileNameValid = fileName.length < MaxFileNameLength
    const isFileSizeValid = file.size < MaxImageFileSize
    const isFileTypeValid = (((ext.toLowerCase() === "bmp") || (ext.toLowerCase() === "jpg") || (ext.toLowerCase() === "jpeg")) && ((fileType.toLowerCase() === "image/bmp") || (fileType.toLowerCase() === "image/jpeg")) )
    this.setState({ isFileNameValid: isFileNameValid, isFileSizeValid: isFileSizeValid, isFileTypeValid: isFileTypeValid })
    if (isFileNameValid && isFileSizeValid) {
      if (isFileTypeValid) {
        var reader = new FileReader()
        const self = this
        reader.onload = function (e) {
          const result = e.target?.result as string || ""
          const stringLength = result.indexOf('base64') + 7
          const fileContent = result.slice(stringLength, result.length)
          if (fileContent) {
            const img = new Image()
            img.onload = function () {
              let prm = createImageBitmap(img)
              prm.then(
                result => {
                  const width = result.width
                  const height = result.height
                  const isLogoSizeValid = ((width <= MaxLogoImageWidth) && (height <= MaxLogoImageHeight))
                  if (isLogoSizeValid) {
                    self.setState({ fileName: file.name, fileContent: fileContent, activeDate: undefined, inactiveDate: undefined, activeDateCanShowInvalid: true, inactiveDateCanShowInvalid: true, isLogoSizeValid: isLogoSizeValid })
                  }
                  else {
                    self.setState({ isLogoSizeValid: isLogoSizeValid })
                    toast.error("Image should be 100 x 100 pixels or lower.")
                  }
                },
                error => { toast.error(error) }
              )
            }
            img.src = result
          } else {
            toast.error("Image file is empty.")
          }
        }
        reader.onerror = function (error) { toast.error(error) }
        reader.readAsDataURL(file)
      } else {
        toast.error("Please upload only .bmp type of image files.")
      }
    }
  }

  handleRightItemClick = (id?: number) => this.setState({ selectionRight: id })

  handleClose = () => this.props.onClose()

  handleSave = () => {
    if (this.state.activeImages.length) {
      const activeImage: ImplementationImage = this.state.activeImages[0]
      this.setState({ saving: true })
      this.saveSubscription && this.saveSubscription.unsubscribe()
      this.saveSubscription = UpdateBrandingImage(this.props.implementationID, activeImage.ImplementationImageID, activeImage.ActiveDate, activeImage.InActiveDate).subscribe(
        message => {
          toast.success(message)
          this.setState({ ...this.emptyState, loading: true })
        },
        error => this.handleAPIError(error, { loading: false }))
    }
  }

  handleUploading = () => {
    const { fileName, fileContent, activeDate, inactiveDate, } = this.state
    if (!fileContent || !activeDate || !inactiveDate) {
      return
    }
    this.setState({ uploading: true, uploadError: false, uploadPercentage: 5, progressBarKey: Math.random().toString() })
    const logo: PortalLogoModel = {
      ImageName: fileName,
      ImageData: fileContent,
      ActiveDate: format(activeDate, "MM/dd/yyyy"),
      InActiveDate: format(inactiveDate, "MM/dd/yyyy"),
      ImplementationID: this.props.implementationID
    }

    this.uploadSubscription && this.uploadSubscription.unsubscribe()
    this.uploadSubscription = PortalLogoInsert(logo).subscribe(
      () => this.setState({ uploading: false, uploadError: false, uploadPercentage: 100, loading: true }),
      error => {
        this.handleAPIError(error, { uploading: false, uploadError: true })
        this.fileUploadSimulation$.error(undefined)
      }
    )
  }

  setActiveImage = (e: any) => {
    const newActive = this.state.selectionRight
    const { activeImages, inactiveImages } = this.state
    const allImages = [...activeImages, ...inactiveImages]
    const newActiveImages = allImages.filter((image: ImplementationImage) => image.ImplementationImageID === newActive)
    const newInactiveImages = allImages.filter((image: ImplementationImage) => image.ImplementationImageID !== newActive)
    this.setState({ activeImages: newActiveImages, inactiveImages: newInactiveImages, selectionRight: undefined })
  }

  handleActiveImageDatesUpdate = (activeDate: Date, inactiveDate: Date) => {
    const activeDateString = format(activeDate, "yyyy-MM-dd'T'HH:mm:ss")
    const inactiveDateString = format(inactiveDate, "yyyy-MM-dd'T'HH:mm:ss")
    const activeImages = this.state.activeImages.map(item => ({ ...item, ActiveDate: activeDateString, InActiveDate: inactiveDateString }))
    this.setState({ activeImages: activeImages, editActiveDate: false })
  }

  render() {
    const {
      loading, saving, editActiveDate,
      fileName, activeImages, inactiveImages, selectionRight,
      activeDate, inactiveDate, activeDateCanShowInvalid, inactiveDateCanShowInvalid,
      progressBarKey, uploadPercentage, fileContent, uploadError, uploading, isFileNameValid, isFileSizeValid, isFileTypeValid, isLogoSizeValid
    } = this.state

    const validActiveDate = !!activeDate
    const validInactiveDate = !!inactiveDate && !!activeDate && activeDate <= inactiveDate
    const canUpload = !!fileContent && validActiveDate && validInactiveDate && isFileNameValid && isFileSizeValid && isFileTypeValid && isLogoSizeValid

    const imageTable = (data: ImplementationImage[], editable: boolean = false) => <Table className="table-sm dark" borderless responsive="bordered-h100p">
      <thead>
        <tr>
          <th>Image</th>
          <th>Active</th>
          <th>Inactive</th>
          {editable && <th>Edit</th>}
        </tr>
      </thead>
      <tbody>
        {data.map(image => <tr key={image.ImplementationImageID} className={selectionRight && selectionRight === image.ImplementationImageID ? "selected" : ""} onClick={() => !editable && this.handleRightItemClick(image.ImplementationImageID)}>
          <td>{image.ImageName}</td>
          <td>{FormatDate(image.ActiveDate)}</td>
          <td>{FormatDate(image.InActiveDate)}</td>
          {editable && <td className="py-0"><Button variant="link" className="py-0" onClick={() => this.setState({ editActiveDate: true })}><FaEdit /></Button></td>}
        </tr>)}
      </tbody>
    </Table>

    return (<>
      <Modal backdrop="static" size="xl" show={this.props.show} onHide={this.handleClose}>
        <Modal.Header>
          <Modal.Title>Manage Images</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Row className="form-group">
              <Form.Label column lg={3} className="text-lg-right" htmlFor="fileName">Branding Image</Form.Label>
              <Col lg={7}>
                <InputGroup>
                  <Form.Control type="text" name="fileName" defaultValue={fileName || ""} readOnly className="bg-white" />
                  <InputGroup.Append>
                    <Button variant="secondary" onClick={() => { this.file && this.file.current && this.file.current.click() }} disabled={uploading}>Browse</Button>
                  </InputGroup.Append>
                </InputGroup>
                <Form.Control type="file" ref={this.file} accept=".bmp, .BMP, .jpg, .JPG, .jpeg, .JPEG" onChange={this.handleFileSelect} style={{ display: 'none' }} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column xs={3} className="text-lg-right" htmlFor="fileName">Active Date</Form.Label>
              <Col xs={9} lg={5} className="text-right text-lg-left">
                <DatePicker className="form-control"
                  ref={this.activeDate}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  popperPlacement="top-end"
                  minDate={new Date()}
                  selected={this.state.activeDate}
                  enableTabLoop={false}
                  onKeyDown={(e) => { if (this.activeDate && this.activeDate.current && e.key === 'Tab') { this.activeDate.current.setOpen(false) } }}
                  onChange={date => this.setState({ activeDate: date as Date, inactiveDate: undefined })}
                  onBlur={() => this.setState({ activeDateCanShowInvalid: true })}
                  customInput={<BirthdayInput isInvalid={activeDateCanShowInvalid && !validActiveDate} />} />
              </Col>
            </Form.Row>
            <Form.Row className="form-group">
              <Form.Label column xs={3} className="text-lg-right" htmlFor="fileName">Inactive Date</Form.Label>
              <Col xs={9} lg={5} className="text-right text-lg-left">
                <DatePicker className="form-control"
                  ref={this.inactiveDate}
                  peekNextMonth
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  popperPlacement="top-end"
                  minDate={this.state.activeDate}
                  selected={this.state.inactiveDate}
                  enableTabLoop={false}
                  onKeyDown={(e) => { if (this.inactiveDate && this.inactiveDate.current && e.key === 'Tab') { this.inactiveDate.current.setOpen(false) } }}
                  onChange={date => this.setState({ inactiveDate: date as Date })}
                  onBlur={() => this.setState({ inactiveDateCanShowInvalid: true })}
                  customInput={<BirthdayInput isInvalid={inactiveDateCanShowInvalid && !validInactiveDate} />} />
              </Col>
              <Col lg={2} className="text-right pt-2 pt-lg-0">
                <Button variant="primary" onClick={this.handleUploading} disabled={!canUpload}>Upload</Button>
              </Col>
            </Form.Row>
            {!isFileSizeValid && <Form.Row><Col className="text-center error-message">Please select file of size maximum 1 MB</Col></Form.Row>}
            {!isFileNameValid && <Form.Row><Col className="text-center error-message">The file name should be less than 200 characters. Please rename the file.</Col></Form.Row>}
            {(!isFileTypeValid || !isLogoSizeValid) && <Form.Row><Col className="text-center error-message">Please select BMP /JPG file 200 x 100 pixels or lower.</Col></Form.Row>}
            {!!fileContent && uploadPercentage !== undefined && <>
              <Form.Row className="form-group">
                <Col lg={{ span: 7, order: 2, offset: 3 }}>
                  <ProgressBar key={progressBarKey} variant={uploadError ? "danger" : "success"} now={uploadPercentage} label={uploadPercentage && uploadPercentage < 100 ? `${uploadPercentage}%` : "Complete"} />
                </Col>
              </Form.Row>
            </>}
            {uploadError && <Form.Row><Col className="text-center error-message">Error Occurred</Col></Form.Row>}
            <Form.Row>
              <Container fluid>
                <Row className="d-none d-lg-flex pt-4">
                  <Col lg={6} className="text-center">Active Image<p><em className="note-message">Only 1 image can be Active at a time</em></p></Col>
                  <Col className="px-0 text-center">
                    {loading && <Spinner animation="border" variant="secondary" />}
                  </Col>
                  <Col lg={5} className="text-center pl-lg-0">Inactive Images</Col>
                </Row>
                <Row>
                  <Col lg={6} className="d-lg-none px-0 text-center">Active Image<p><em className="note-message">Only 1 image can be Active at a time</em></p></Col>
                  <Col lg={6} className="px-0">{imageTable(activeImages, true)}</Col>
                  <Col className="px-0 text-center align-self-center">
                    <Button className="px-3 my-2 my-lg-0" disabled={!selectionRight} onClick={this.setActiveImage}>
                      <FaChevronLeft className="d-none d-lg-flex" />
                      <FaChevronUp className="d-lg-none" />
                    </Button>
                  </Col>
                  <Col lg={5} className="d-lg-none px-0 text-center">Inactive Images</Col>
                  <Col lg={5} className="px-0">{imageTable(inactiveImages)}</Col>
                </Row>
              </Container>
            </Form.Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={this.handleClose}>Cancel</Button>
          <ActionButton variant="primary" className="ml-2" onClick={this.handleSave} disabled={!activeImages.length} spin={saving} disableOnSpin>Submit</ActionButton>
        </Modal.Footer>
      </Modal>
      {editActiveDate && activeImages[0] && <EditActiveImageDate show={editActiveDate}
        activeDate={activeImages[0].ActiveDate || ""}
        inactiveDate={activeImages[0].InActiveDate || ""}
        onCancel={() => this.setState({ editActiveDate: false })}
        onUpdate={this.handleActiveImageDatesUpdate} />}
    </>)
  }
}
